import { Row, Space } from "antd";
import { LimitMinMaxSize } from "components/FixtureView/components/LimitMinMaxSize.tsx";
import { EditModeToggle } from "components/Shared/EditModeToggle";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { SearchInput } from "components/Shared/SearchInput";
import { PeopleTypes } from "enums/peopleTypes";
import { useAddFixtureRosterPlayers, useDeleteFixtureRosterPlayers } from "hooks/useFixtureRosterApi";
import { useRecordsState } from "hooks/useRecordsState";
import { useUpdateFixtureRosterPlayer } from "hooks/useUpdateRosterPlayer";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { filterPersonList } from "utils/filterPersonList";
import { getPersonDeleteApiData } from "utils/getPersonDeleteApiData";
import { runAsyncAction } from "utils/runAsyncAction";
import { useFixtureRosterColumns } from "../hooks/useFixtureRosterColumns";
import { CopyPeople } from "./CopyPeople";

type Props = {
  isLoading: boolean;
  fixture: any;
  roster: Array<any>;
  viewOnly: boolean;
};

export const FixtureRosterTab = ({ isLoading, fixture, roster, viewOnly }: Props) => {
  const { entityId: entityIdFromParams, seasonId: seasonIdFromParams, fixtureId: fixtureIdFromParams } = useParams();
  const entityId = entityIdFromParams || "undefined";
  const seasonId = seasonIdFromParams || "undefined";
  const fixtureId = fixtureIdFromParams || "undefined";
  const { formatMessage } = useIntl();
  const [searchText, setSearchText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const filteredRoster = filterPersonList(roster, searchText);
  const starterPlayerCount = roster.filter((x: any) => x.isStarter)?.length;
  const selectedPlayerCount = roster.filter((x: any) => x.isActive)?.length;
  const maxFixtureRosterSize = fixture?.maximumRosterSize || 0;
  const maxFixtureRosterStarterSize = fixture?.maximumRosterStarterSize || 0;

  const { isPending: areRecordsAdding, mutateAsync: addRecordsApi } = useAddFixtureRosterPlayers();
  const { isPending: areRecordsDeleting, mutateAsync: deleteRecordsApi } = useDeleteFixtureRosterPlayers();
  const {
    updatingRecord,
    isUpdating: isRecordBeingUpdated,
    mutateAsync: updateRecord,
  } = useUpdateFixtureRosterPlayer();

  const { checkIfRecordBeingBusy, ensureRecordsBusy } = useRecordsState({
    isAddingOrDeleting: areRecordsAdding || areRecordsDeleting,
    isUpdating: isRecordBeingUpdated,
    updatingRecord,
    applyForAll: !fixture?.taskStatus,
  });

  const areUserControlsDisabled =
    isLoading || areRecordsAdding || areRecordsDeleting || !fixture?.areRosterAndStaffEditable;

  const addRecords = (records: Array<any>) => {
    if ((records?.length || 0) === 0) {
      return;
    }
    runAsyncAction(
      ensureRecordsBusy(records, async () =>
        addRecordsApi(
          {
            entityId,
            seasonId,
            fixtureId,
            data: {
              players: records.map((x: any) => ({
                dcPersonId: x?.dcPersonId,
                personId: x?.personId,
                isCaptain: x?.isCaptain,
                isStarter: x?.isStarter,
                position: x?.position,
                number: x?.number,
              })),
            },
          },
          {
            onSuccess: () =>
              records.forEach((x: any) => {
                x.isActive = true;
              }),
          }
        )
      ),
      "Failed to add fixture roster records"
    );
  };

  const deleteRecords = (records: Array<any>) => {
    if ((records?.length || 0) === 0) {
      return;
    }
    runAsyncAction(
      ensureRecordsBusy(records, async () =>
        deleteRecordsApi(
          {
            entityId,
            seasonId,
            fixtureId,
            data: getPersonDeleteApiData(records),
          },
          {
            onSuccess: () =>
              records.forEach((x: any) => {
                x.isActive = false;
              }),
          }
        )
      ),
      "Failed to delete fixture roster records"
    );
  };

  const columns = useFixtureRosterColumns({
    viewOnly: viewOnly || !fixture?.areRosterAndStaffEditable,
    editMode,
    checkIfRecordBeingBusy,
    fixture,
    updateRecord: (data: any, record: any) => {
      runAsyncAction(
        updateRecord({
          entityId,
          seasonId,
          fixtureId,
          personId: record?.personId,
          dcPersonId: record?.dcPersonId,
          data,
          record,
        }),
        "Failed to update fixture roster record"
      );
    },
    addRecord: (record: any) => addRecords([record]),
    deleteRecord: (record: any) => deleteRecords([record]),
  });

  return (
    <>
      <Row className="mb-10" justify="space-between" align="middle">
        <Space direction="horizontal">
          <LimitMinMaxSize
            title={formatMessage({ id: "fixtureRosterPage.rosterSize" })}
            actualSize={selectedPlayerCount}
            maxSize={maxFixtureRosterSize}
          />
          <LimitMinMaxSize
            title={formatMessage({ id: "fixtureRosterPage.starterSize" })}
            actualSize={starterPlayerCount}
            minSize={maxFixtureRosterStarterSize}
            maxSize={maxFixtureRosterStarterSize}
          />
        </Space>
        <Space>
          {!viewOnly && (
            <>
              <EditModeToggle disabled={areUserControlsDisabled} onChange={setEditMode} />
              <CopyPeople
                type={PeopleTypes.Roster}
                disabled={areUserControlsDisabled}
                entityId={entityId}
                seasonId={seasonId}
                currentFixtureId={fixtureId}
              />
            </>
          )}
          <SearchInput onSearch={setSearchText} />
        </Space>
      </Row>
      <Row>
        <RosterAndStaffTable isLoading={isLoading} records={filteredRoster} columns={columns} />
      </Row>
    </>
  );
};
